import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Button,
  Dropdown,
  Grid,
  Icon,
  Input,
  Label,
  Segment,
} from 'semantic-ui-react';
import React from 'react';
import { Col, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import COLORS from '../../constants/colors';
import { GRADES, isValidEmail, STATUS_COLOR } from './utils';
import { DateTime } from 'luxon';

const LeadDetails = ({ prospectData, editData, setEditData, onUpdateLeadDetails }) => {
  const {
    status,
    timezone,
    teacher_name: teacherName,
    region,
    username,
    cancellation_reason: cancellationReason,
    confirmation_time: confirmationTime,
    cancelled_on: cancelledOn,
    demo_time: demoTime,
    next_follow_up: nextFollowUp,
    source,
    available_timezones: allTimezones = [],
  } = prospectData || {};
  const { canEditData, editedFields, editedValues } = editData || {};
  const [activeIndex, setActiveIndex] = React.useState(0);

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;

    setActiveIndex(newIndex);
  };

  const handleDropdownChange = (e, k) => {
    const { name, value } = k;
    setEditData(prev => ({
      ...prev,
      editedValues: { ...prev.editedValues, [name]: value },
    }));
  };

  const handleChange = (e, { name, value }) => {
    setEditData(prev => ({
      ...prev,
      editedValues: { ...prev.editedValues, [name]: value },
    }));
  };

  const resetEditData = () => {
    setEditData(prev => ({
      canEditData: false,
      editedFields: [],
      editedValues: { ...prev.editedValues },
    }));
  };

  const isUpdateButtonDisabled =
    Object.keys(editedValues).length === 0 ||
    Object.keys(editedValues).every(key => editedValues[key] === prospectData[key]) ||
    (editedValues.email && !isValidEmail(editedValues.email));

  const formattedCancelledOn = cancelledOn
    ? DateTime.fromSeconds(cancelledOn, { zone: 'utc' })
        .toLocal()
        .toFormat("MMM dd, yyyy 'at' hh:mm a z")
    : 'N/A';

  const formattedConfirmationTime = confirmationTime
    ? DateTime.fromSeconds(confirmationTime, { zone: 'utc' })
        .toLocal()
        .toFormat("MMM dd, yyyy 'at' hh:mm a z")
    : 'N/A';

  const formattedDemotimeLocally = demoTime
    ? DateTime.fromSeconds(demoTime, { zone: 'utc' })
        .toLocal()
        .toFormat("MMM dd, yyyy 'at' hh:mm a z")
    : 'N/A';

  const formattedDemotimeUserTimeZone = demoTime
    ? DateTime.fromSeconds(demoTime, { zone: timezone }).toFormat(
        "MMM dd, yyyy 'at' hh:mm a z",
      )
    : 'N/A';

  return (
    <Accordion>
      <AccordionTitle active={activeIndex === 0} index={0} onClick={handleClick}>
        <Row alignItems="center" justifyContent="space-between">
          <Col>
            <Text variant="body1Medium">Lead Details</Text>
          </Col>
          <Col>
            <Icon name="dropdown" />
          </Col>
        </Row>
      </AccordionTitle>
      <AccordionContent active={activeIndex === 0}>
        <Grid columns={2}>
          <Grid.Column>
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Status
              </Text>
              <Separator height={4} transparent />
              <Label
                color={STATUS_COLOR[status] || 'grey'}
                size="small"
                style={{ marginTop: '-10' }}
              >
                {status || 'N/A'}
              </Label>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Email ID
              </Text>
              <Separator height={4} transparent />
              <Input
                disabled={!editedFields.includes('email')}
                fluid
                value={editedValues.email}
                onChange={handleChange}
                name="email"
              />
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Grade
              </Text>
              <Separator height={4} transparent />
              <Dropdown
                fluid
                floating
                selection
                onChange={handleDropdownChange}
                options={GRADES}
                value={editedValues.grade}
                name="grade"
                disabled={!editedFields.includes('grade')}
              />
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Timezone
              </Text>
              <Separator height={4} transparent />
              <Dropdown
                fluid
                floating
                selection
                onChange={handleDropdownChange}
                options={allTimezones}
                value={editedValues.timezone}
                name="timezone"
                disabled={!editedFields.includes('timezone')}
              />
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Current Tutor
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{teacherName || 'N/A'}</Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Cancellation Reason
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{cancellationReason || 'N/A'}</Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Follow up On
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{nextFollowUp || 'N/A'}</Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Source
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{source || 'N/A'}</Text>
            </Col>
          </Grid.Column>
          <Grid.Column>
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Parent Name
              </Text>
              <Separator height={4} transparent />
              <Input
                disabled={!editedFields.includes('parent_name')}
                fluid
                value={editedValues.parent_name}
                onChange={handleChange}
                name="parent_name"
              />
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Student Name
              </Text>
              <Separator height={4} transparent />
              <Input
                disabled={!editedFields.includes('student_name')}
                fluid
                value={editedValues.student_name}
                onChange={handleChange}
                name="student_name"
              />
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Region
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{region || 'N/A'}</Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Requested Trial Slot
              </Text>
              <Separator height={4} transparent />
              {demoTime ? (
                <>
                  <Text variant="cta2">{formattedDemotimeLocally}</Text>
                  <Text variant="body3" color={COLORS.GRAY}>
                    ({formattedDemotimeUserTimeZone})
                  </Text>
                </>
              ) : (
                <Text variant="cta2">N/A</Text>
              )}
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Trial Cancelled On
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2" color={COLORS.RUSTY_RED}>
                {formattedCancelledOn}
              </Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Trial Confirmed On
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{formattedConfirmationTime}</Text>
            </Col>
            <Separator height={16} transparent />
            <Col>
              <Text variant="cta2" color={COLORS.GRAY}>
                Username and Password
              </Text>
              <Separator height={4} transparent />
              <Text variant="cta2">{username || 'N/A'}</Text>
            </Col>
          </Grid.Column>
        </Grid>
        <Separator height={16} transparent />
        {canEditData && (
          <Segment basic textAlign="right">
            <Button basic size="large" onClick={resetEditData}>
              Cancel
            </Button>
            <Button
              size="large"
              color="black"
              disabled={isUpdateButtonDisabled}
              onClick={onUpdateLeadDetails}
            >
              Update Details
            </Button>
          </Segment>
        )}
      </AccordionContent>
    </Accordion>
  );
};

export default LeadDetails;
